<template>
    <div>
        <span title="About this map" v-on:click="aboutPage = true" class="about">
            <font-awesome-icon icon="info"/>
        </span>
      <transition name="fade">
        <div v-if="aboutPage" v-on:click.self="aboutPage = false" class="aboutPage">
          <div class="aboutPage-content">
            <span v-on:click="aboutPage = false" style="float: right"> <font-awesome-icon icon="times"/></span>
            <h1>Southeast Michigan Demographic Emphasis Areas</h1>
            <div style="display:none; float: right;">
              <iframe style=" max-width: 100%;" width="560" height="315" src="https://www.youtube.com/embed/SWp11latzFA"
                      frameborder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowfullscreen></iframe>
            </div>
            <h2>About</h2>
            <p>SEMCOG’s Demographic Emphasis
              Areas tool is an interactive application that provides a detailed summary of key demographic variables at
              different geographies (regional, county, community, and census tract) across the seven-county region. The
              tool provides one location where communities, partner organizations, and residents can learn about our
              current socioeconomic indicators and have a data-driven understanding of community needs to assist in
              effective planning.</p>
            <p>
              For example, with the help of this tool, a county or community’s senior services department can partner with
              developers to develop accessible and affordable housing projects for seniors with disabilities. Selecting
              “older adults’ and “disability” tiles together, stakeholders can identify areas with highest
              concentrations of seniors with disabilities. Based on this knowledge, community officials can entice
              developers to build affordable and accessible senior housing units.
            </p>
            <h2>Methodology</h2>
            <div v-if="!inInerMode">
              <p>SEMCOG’s Demographic Emphasis Areas analysis uses a score based analysis to depict and symbolize the
                concentrations of various populations and groups in the region.</p>
              <p>The score calculation is determined by standard deviations relative to a criteria's regional average.
                Standard deviation measures the dispersion of a dataset relative to its mean. This standard deviation
                based score classifies the concentration of the diverse populations in relation to the region’s mean for
                every community and census tract in the region.</p>
              <p>The data for each of the criteria in the demographic emphasis areas analysis are split into five "bins"
                based on the relative concentration across the region, ranging from 0 (low concentration) to 4 (high
                concentration). Bin 2 for each indicator contains the community or the census tracts at or near (within
                a half standard deviation from) the regional average (mean) for that indicator. Bins 4, 3, 1, and 0 are
                then built out from the regional average; Bins 1 and 3 go one full standard deviation out from bin 2,
                and bins 0 and 4 contain any remaining tracts further out from 1 or 3, respectively.</p>
              <img class="center" src="images/equity_histogram.png" alt="histogram">
              <p>When combining multiple indicators, the scores for all the selected indicators are averaged to obtain
                the final composite score.</p>
            </div>
            <div v-if="inInerMode">
              <p>SEMCOG’s Demographic Emphasis Areas analysis uses a score based analysis to depict and symbolize the
                concentrations of various vulnerable groups in the region.</p>
              <p>The score calculation based on <strong>Standard Deviation</strong> measures the dispersion of an indicator relative to
                the regional average. This standard deviation based score classifies the concentration of the Demographic
                populations in relation to the region’s mean for every community and census tract in the region.</p>
              <p>The data for each of the criteria in the demographic emphasis areas analysis are split into five "bins"
                based on the relative concentration across the region, ranging from 0 (low concentration) to 4 (high
                concentration). Bin 2 for each indicator contains the community or the census tracts at or near (within
                a half standard deviation from) the regional average (mean) for that indicator. Bins 4, 3, 1, and 0 are
                then built out from the regional average; Bins 1 and 3 go one full standard deviation out from bin 2,
                and bins 0 and 4 contain any remaining tracts further out from 1 or 3, respectively.</p>
              <img class="center" src="images/equity_histogram.png" alt="histogram">
              <p>Standard deviation reflects the amount of variability in a given dataset and is useful in identifying
                extreme values.</p>
              <p>The composite score is calculated by averaging the scores of the selected indicators.</p>
            </div>
            <h2>Composite Chart</h2>
            <p>If more than one dataset is selected, a composite radar chart is shown at the top of the page. This chart
              shows all of the selected dataset values for the selected geography. The scores are plotted on the chart
              according to their value. For example, if a dataset has a score value of 0 it will be at the center of the
              chart and if it has a score of 4 it will be on the edge of the chart. The dashed line shows the averaged
              composite score. You can use the individual score values and the composite score to see which datasets are
              driving the composite score for that geography. Hover over the color labels to see the percentage for
              the dataset, and use the legend to toggle the lines on or off. See the example below:</p>
            <img class="center" style="width: 25%" src="images/composite_chart_tract_5302.png" alt="chart example">
            <br>
            <h2>Modified Census Variable Definitions</h2>
            <p><strong>Disability:</strong> Disability includes persons with any of the following disabilities –
              hearing, vision, cognitive, ambulatory, self-care, or independent living difficulty.</p>
            <p><strong>Disengaged Youth:</strong> Population aged 16 to 19 years who are not enrolled in school, not
              employed and/or not in the labor force.</p>
            <p><strong>Female Headed Households:</strong> A female maintaining a household with no husband of the
              householder present. </p>
            <p><strong>Foreign Born:</strong> Foreign born refers to people who are not U.S. citizens at birth. This
              includes naturalized U.S. citizens, lawful permanent residents (immigrants), temporary migrants (such as
              foreign students), humanitarian migrants (such as refugees and asylees), and persons illegally present in
              the United States.</p>
            <p><strong>Hispanic:</strong> Refers to a person of Cuban, Mexican, Puerto Rican, South or Central American,
              or other Spanish culture or origin regardless of race.</p>
            <p><strong>Households in Poverty:</strong> If a household’s total income is less than the federal poverty
              threshold, then that household and every individual in it is considered in poverty.</p>
            <p><strong>Housing Cost Burden:</strong> HUD defines cost-burdened households as those “who pay more than 30
              percent of their income for housing” and “may have difficulty affording necessities such as food,
              clothing, transportation, and medical care.”</p>
            <p><strong>Limited English Proficiency:</strong> A "limited English speaking household" is one in which no
              member 14 years old and over (1) speaks only English or (2) speaks a non-English language and speaks
              English "very well." In other words, all members 14 years old and over have at least some difficulty with
              English.</p>
            <p><strong>Minority:</strong> Includes people that are of “Black or African American,” “American Indian or
              Alaska Native,” “Asian,” and “Native Hawaiian or Other Pacific Islander” race categories. Persons
              reporting as multiracial, mixed, interracial, or a Hispanic, Latino, or Spanish group are also included in
              this category. Any person who is not a “Non-Hispanic White” is considered as a Minority.</p>
            <p><strong>No Car Households:</strong> Households with no vehicles. These households are also transit
              dependent.</p>
            <p><strong>Non-Hispanic Asian:</strong> Includes people having origins in any of the following - Far East,
              Southeast Asia, or the Indian subcontinent and did not report being of Hispanic origin.</p>
            <p><strong>Non-Hispanic Black:</strong> Includes people who reported Black and no other race group and did
              not report being of Hispanic origin.</p>
            <p><strong>Older Adults:</strong> Population aged 65 years or older.</p>
            <p><strong>Other Non-White Non-Hispanic Races:</strong> Includes all other races not included in the
              “Non-Hispanic
              White,” “Non-Hispanic Black or African American,” and “Non-Hispanic Asian”. People belonging to any
              Hispanic ethnicity or origin are also not included in this category.</p>
            <p><strong>Persons in Poverty:</strong> Census Bureau’s poverty status is determined for all persons except
              institutionalized persons, persons in military group quarters and in college dormitories, and unrelated
              individuals under 15 years old. </p>
            <p><strong>Transit Dependent Households:</strong> Households with no vehicle or number of vehicles less than
              the number of workers in the household.</p>
            <p><strong>Youth:</strong> Population aged 0 to 17 years.</p>

            <div>
              <h2>Project Presets</h2>
              <p>The Project Preset dropdown is a shortcut to view different combinations of indicators. The preset is
                meant to serve
                as a way to view the types of data that are associated with that particular project. The
                Transportation Analysis preset contains the eight socioeconomic indicators that are likely to include
                populations that require additional mobility considerations, such as greater reliance on the bicycle and
                pedestrian network or increased need for safety and security considerations. They are the following:
              </p>
              <ol>
                <li> <strong>Disability:</strong> People with a disability may have trouble operating a motor vehicle, taking transit, or may have
                  other mobility difficulties.
                </li>
                <li><strong>Female-Headed Households:</strong> Female heads of households may have real or perceived mobility issues related to personal safety.
                </li>
                <li><strong>Households in Poverty:</strong> Households in poverty may not be able to afford to own and/or operate a private automobile.</li>
                <li><strong>Limited English Proficiency:</strong> People with limited English proficiency may have difficulty reading signs or instructions while
                  driving or using transit.
                </li>
                <li><strong>Minority:</strong> Minorities have historically been negatively impacted by transportation projects and may rely on
                  alternative transportation.
                </li>
                <li><strong>Older Adults:</strong> Older adults may not be able to operate a private automobile and may have other mobility
                  difficulties.
                </li>
                <li><strong>Transit-Dependent Households:</strong> People reliant on transit generally have more mobility difficulties and may rely on walking and
                  biking options.
                </li>
                <li><strong>Youth:</strong> Younger people may not be able to own and/or operate an automobile and may be more reliant on
                  walking and biking options.
                </li>
              </ol>
            </div>
            <h2>Sources</h2>
              <ul>
                <li><a href="https://www.census.gov/programs-surveys/acs" target="_blank">Census Bureau's American
                  Community Survey (ACS) 2020-2024 5-Year Estimates</a>
                </li>
              </ul>
            <h2>Questions?</h2>
            <ul>
              <li>For questions about this application: <a href="mailto:misiuk@semcog.org" target="_blank">Chad Misiuk</a>
              </li>
              <li>For questions about the data: <a href="mailto:uppalapati@semcog.org" target="_blank">Sirisha Uppalapati</a>
              </li>
            </ul>
          </div>
        </div>
      </transition>

    </div>

</template>

<script>
    export default {
        name: "AboutPage",
      props: ['inInerMode', 'selectedId'],
        data: function () {
            return {
                aboutPage: this.selectedId === null,
            }
        },
      watch: {
        aboutPage: function () {
          this.$emit('aboutPage', this.aboutPage)
        },
      },
    }
</script>

<style scoped>

    .about {
      border-radius: 5px;
      padding-left: 5px;
      padding-right: 5px;
      padding-top: 2px;
      padding-bottom: 2px;
      border: 2px solid black;
      background-color: white;
    }

    .aboutPage {
        position: fixed;
        background: rgba(46, 60, 67, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 10;
        opacity: 1;
        overflow: hidden;
    }

    .aboutPage-content {
        background: white;
        max-width: 80%;
        border: #fffcfc solid 2px;
        border-radius: 10px;
        padding: 10px 30px;
        max-height: 80%;
        overflow: auto;
    }

    .center {
      display: block;
      margin-left: auto;
      margin-right: auto;
      width: 50%;
    }

    .fade-enter-active, .fade-leave-active {
        transition: opacity 1s;
    }

    .fade-enter, .fade-leave-to
    {
        opacity: 0;
    }

    .fade-enter-active .aboutPage-content, .fade-leave-active .aboutPage-content {
        transition: transform 1s;
    }

    .fade-enter .aboutPage-content, .fade-leave-to .aboutPage-content {
        transform: translate(50vw,-50vh) scale(0);
    }

    .fade-enter-to .aboutPage-content, .fade-leave-to .aboutPage-content {
        overflow: hidden;
    }

</style>
